import { Candidate, CandidateExperience, ExternalCandidateEducation } from './candidate';
import { Pagination } from './pagination';

export const CHATBOT_RESULTS_PAGE_SIZE = 10;
export const CHATBOT_RESULTS_MAX_FILL_TRIES = 25;

export interface ChatbotCandidatesFillState {
  response: ChatbotCandidatesApiResponse;
  previousLength: number;
  tries: number;
}

export interface ChatbotContactAvailability {
  phones: boolean;
  work_email: boolean;
  recommended_personal_email: boolean;
  personal_emails: boolean;
  mobile_phone: boolean;
}

export interface ChatbotExperience {
  title: string;
  description: string | null;
  company_name: string;
  date_from: string | null;
  date_to: string | null;
  linkedin_url: string | null;
  is_primary: boolean | null;
}

export interface ChatbotEducationEntry {
  title: string;
  major: string | null;
  degree: string | null;
  date_from: string | null;
  date_to: string | null;
}

export interface ChatbotCandidateApiItem {
  tenant: string;
  rawtxt: string;
  skills: string;
  title: string | null;
  yoe: number | null;
  degree: string | null;
  location: string | null;
  industry: string | null;
  company: string | null;
  experiences: ChatbotExperience[];
  external_id: string;
  soft_skills: string;
  full_name: string;
  linkedin_url: string | null;
  current_company: string | null;
  education_entries: ChatbotEducationEntry[];
  contact_availability: ChatbotContactAvailability;
  weighted_score: number;
  criteria_met: string[];
  criteria_not_met: string[];
  criteria_reasoning: Record<string, string>;
  must_have_failed: boolean;
  text_score: number;
  title_score: number;
  ai_summary: string | null;
}

export interface ChatbotCandidatesApiResponse {
  content: ChatbotCandidateApiItem[];
  totalElements: number;
  page: number;
  hasMore: boolean;
}

function splitSkillsString(skills: string): string[] {
  if (!skills) return [];
  return skills
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

function mapExperience(exp: ChatbotExperience): CandidateExperience {
  return {
    companyName: exp.company_name || '',
    dateFrom: exp.date_from ?? null,
    dateTo: exp.date_to ?? null,
    title: exp.title || '',
    description: exp.description ?? undefined,
  };
}

function mapEducation(entry: ChatbotEducationEntry): ExternalCandidateEducation {
  return {
    title: entry.title || '',
    major: entry.major || '',
    degree: entry.degree || '',
    dateFrom: entry.date_from || '',
    dateTo: entry.date_to || '',
  };
}

export function chatbotCandidateToCandidate(item: ChatbotCandidateApiItem): Candidate {
  return {
    id: item.external_id,
    fullName: item.full_name || item.external_id,
    linkedinUrl: item.linkedin_url || '',
    currentAddress: item.location || '',
    yearsOfExperience: item.yoe ?? undefined,
    hardSkills: splitSkillsString(item.skills),
    softSkills: splitSkillsString(item.soft_skills),
    experiences: (item.experiences ?? []).map(mapExperience),
    education: (item.education_entries ?? []).map(mapEducation),
    personalEmails: item.contact_availability?.personal_emails ?? false,
    workEmail: item.contact_availability?.work_email ?? false,
    mobilePhone: item.contact_availability?.mobile_phone ?? false,
    phones: item.contact_availability?.phones ?? false,
    score: item.weighted_score,
    bio: item.ai_summary ?? undefined,
    label: item.title ?? undefined,
    currentCompany: item.current_company ?? undefined,
    mustHaveFailed: item.must_have_failed,
    criteriaMet: item.criteria_met ?? [],
    criteriaNotMet: item.criteria_not_met ?? [],
    criteriaReasoning: item.criteria_reasoning ?? {},
    weightedScore: item.weighted_score,
  };
}

export function chatbotResponseToPagination(
  response: ChatbotCandidatesApiResponse,
  loading = false,
): Pagination<Candidate> {
  // A complete ten-row page enables the next request. Legacy sessions can still
  // return twenty rows; a short page has exhausted the UI's fill retries.
  const hasNextPage = response.hasMore && response.content.length >= CHATBOT_RESULTS_PAGE_SIZE;
  const pageSize = response.content.length || CHATBOT_RESULTS_PAGE_SIZE;
  const totalPages = hasNextPage ? response.page + 2 : response.page + 1;

  return {
    content: response.content.map(chatbotCandidateToCandidate),
    totalElements: response.totalElements,
    totalPages,
    size: pageSize,
    number: response.page,
    numberOfElements: response.content.length,
    first: response.page === 0,
    last: !hasNextPage,
    empty: response.content.length === 0,
    loading,
  };
}
